import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { FaTint, FaArrowLeft, FaInfoCircle, FaSun, FaDumbbell } from 'react-icons/fa';
import SEO, { PageSEO, StructuredData } from '../../components/SEO';

const WaterIntakeCalculator = () => {
  const [unit, setUnit] = useState('metric');
  const [weight, setWeight] = useState('');
  const [activityLevel, setActivityLevel] = useState('moderate');
  const [climate, setClimate] = useState('temperate');
  const [result, setResult] = useState(null);

  const activityLevels = [
    { value: 'sedentary', label: 'Sedentary', description: 'Little to no exercise', multiplier: 1.0 },
    { value: 'light', label: 'Lightly Active', description: '1-3 days/week', multiplier: 1.1 },
    { value: 'moderate', label: 'Moderately Active', description: '3-5 days/week', multiplier: 1.2 },
    { value: 'active', label: 'Very Active', description: '6-7 days/week', multiplier: 1.3 },
    { value: 'athlete', label: 'Athlete', description: 'Intense daily training', multiplier: 1.5 },
  ];

  const climates = [
    { value: 'cold', label: 'Cold', multiplier: 0.9 },
    { value: 'temperate', label: 'Temperate', multiplier: 1.0 },
    { value: 'hot', label: 'Hot & Humid', multiplier: 1.2 },
  ];

  const calculateWaterIntake = (e) => {
    e.preventDefault();

    let weightInKg;

    if (unit === 'metric') {
      weightInKg = parseFloat(weight);
    } else {
      weightInKg = parseFloat(weight) * 0.453592;
    }

    // Base calculation: 30-35 ml per kg of body weight
    const baseWaterMl = weightInKg * 33;

    // Apply activity multiplier
    const activityMultiplier = activityLevels.find((a) => a.value === activityLevel).multiplier;
    const climateMultiplier = climates.find((c) => c.value === climate).multiplier;

    const totalWaterMl = baseWaterMl * activityMultiplier * climateMultiplier;
    const totalWaterL = totalWaterMl / 1000;

    // Calculate glasses (250ml each)
    const glasses = Math.round(totalWaterMl / 250);

    // Calculate bottles (500ml each)
    const bottles = Math.round(totalWaterMl / 500);

    // Hourly intake (assuming 16 waking hours)
    const hourlyMl = Math.round(totalWaterMl / 16);

    // Schedule breakdown
    const schedule = [
      { time: 'Morning (Wake up)', amount: Math.round(totalWaterMl * 0.15), note: 'Start your day hydrated' },
      { time: 'Mid-Morning', amount: Math.round(totalWaterMl * 0.2), note: 'Before/after breakfast' },
      { time: 'Afternoon', amount: Math.round(totalWaterMl * 0.25), note: 'Around lunch time' },
      { time: 'Late Afternoon', amount: Math.round(totalWaterMl * 0.2), note: 'Pre-workout hydration' },
      { time: 'Evening', amount: Math.round(totalWaterMl * 0.15), note: 'With dinner' },
      { time: 'Night', amount: Math.round(totalWaterMl * 0.05), note: 'Before bed (not too much!)' },
    ];

    setResult({
      totalMl: Math.round(totalWaterMl),
      totalL: totalWaterL.toFixed(1),
      glasses,
      bottles,
      hourlyMl,
      schedule,
    });
  };

  const resetCalculator = () => {
    setWeight('');
    setResult(null);
  };

  const waterStructuredData = StructuredData.calculator(
    'Water Intake Calculator',
    'Calculate how much water you should drink daily for optimal health.',
    '/calculators/water-intake'
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO {...PageSEO.waterIntake} structuredData={waterStructuredData} />
      {/* Header */}
      <section className="bg-gradient-to-br from-cyan-500 to-blue-500 text-white py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/calculators" className="inline-flex items-center text-cyan-100 hover:text-white mb-6 transition-colors">
            <FaArrowLeft className="mr-2" />
            All Calculators
          </Link>
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-white/10 rounded-xl flex items-center justify-center">
              <FaTint className="text-2xl" />
            </div>
            <div>
              <h1 className="text-3xl md:text-4xl font-bold">Water Intake Calculator</h1>
              <p className="text-cyan-100 mt-1">Find your daily hydration needs</p>
            </div>
          </div>
        </div>
      </section>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid md:grid-cols-2 gap-8">
          {/* Calculator Form */}
          <div className="bg-white rounded-2xl shadow-sm p-6 md:p-8 border border-gray-100">
            <h2 className="text-xl font-bold text-gray-900 mb-6">Enter Your Details</h2>

            {/* Unit Toggle */}
            <div className="flex bg-gray-100 rounded-lg p-1 mb-6">
              <button
                type="button"
                onClick={() => setUnit('metric')}
                className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                  unit === 'metric' ? 'bg-white shadow text-primary-600' : 'text-gray-600'
                }`}
              >
                Metric (kg)
              </button>
              <button
                type="button"
                onClick={() => setUnit('imperial')}
                className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                  unit === 'imperial' ? 'bg-white shadow text-primary-600' : 'text-gray-600'
                }`}
              >
                Imperial (lbs)
              </button>
            </div>

            <form onSubmit={calculateWaterIntake} className="space-y-5">
              {/* Weight */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Weight ({unit === 'metric' ? 'kg' : 'lbs'})
                </label>
                <input
                  type="number"
                  value={weight}
                  onChange={(e) => setWeight(e.target.value)}
                  placeholder={unit === 'metric' ? 'e.g., 70' : 'e.g., 154'}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
                  required
                  min="30"
                  max={unit === 'metric' ? '200' : '440'}
                />
              </div>

              {/* Activity Level */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <FaDumbbell className="inline mr-2" />
                  Activity Level
                </label>
                <select
                  value={activityLevel}
                  onChange={(e) => setActivityLevel(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
                >
                  {activityLevels.map((level) => (
                    <option key={level.value} value={level.value}>
                      {level.label} - {level.description}
                    </option>
                  ))}
                </select>
              </div>

              {/* Climate */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <FaSun className="inline mr-2" />
                  Climate
                </label>
                <div className="grid grid-cols-3 gap-2">
                  {climates.map((c) => (
                    <button
                      key={c.value}
                      type="button"
                      onClick={() => setClimate(c.value)}
                      className={`py-3 px-2 rounded-xl border-2 font-medium text-sm transition-colors ${
                        climate === c.value
                          ? 'border-cyan-500 bg-cyan-50 text-cyan-700'
                          : 'border-gray-200 text-gray-600 hover:border-gray-300'
                      }`}
                    >
                      {c.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  type="submit"
                  className="flex-1 bg-gradient-to-r from-cyan-500 to-blue-500 text-white py-3 px-6 rounded-xl hover:from-cyan-600 hover:to-blue-600 transition-colors font-semibold"
                >
                  Calculate
                </button>
                {result && (
                  <button
                    type="button"
                    onClick={resetCalculator}
                    className="px-6 py-3 border border-gray-300 rounded-xl hover:bg-gray-50 transition-colors font-medium text-gray-700"
                  >
                    Reset
                  </button>
                )}
              </div>
            </form>
          </div>

          {/* Results */}
          <div className="bg-white rounded-2xl shadow-sm p-6 md:p-8 border border-gray-100">
            <h2 className="text-xl font-bold text-gray-900 mb-6">Your Daily Water Intake</h2>

            {result ? (
              <div className="space-y-6">
                {/* Main Result */}
                <div className="bg-gradient-to-br from-cyan-500 to-blue-500 rounded-xl p-6 text-white text-center">
                  <div className="text-cyan-100 text-sm mb-1">You should drink</div>
                  <div className="text-5xl font-bold mb-1">{result.totalL}</div>
                  <div className="text-cyan-100 text-lg">liters per day</div>
                </div>

                {/* Quick Stats */}
                <div className="grid grid-cols-3 gap-3">
                  <div className="bg-cyan-50 rounded-xl p-3 text-center">
                    <div className="text-2xl font-bold text-cyan-700">{result.glasses}</div>
                    <div className="text-xs text-gray-600">glasses</div>
                    <div className="text-xs text-gray-500">(250ml)</div>
                  </div>
                  <div className="bg-blue-50 rounded-xl p-3 text-center">
                    <div className="text-2xl font-bold text-blue-700">{result.bottles}</div>
                    <div className="text-xs text-gray-600">bottles</div>
                    <div className="text-xs text-gray-500">(500ml)</div>
                  </div>
                  <div className="bg-indigo-50 rounded-xl p-3 text-center">
                    <div className="text-2xl font-bold text-indigo-700">{result.hourlyMl}</div>
                    <div className="text-xs text-gray-600">ml/hour</div>
                    <div className="text-xs text-gray-500">(awake)</div>
                  </div>
                </div>

                {/* Schedule */}
                <div>
                  <h3 className="font-semibold text-gray-900 mb-3">Suggested Schedule</h3>
                  <div className="space-y-2">
                    {result.schedule.map((item, index) => (
                      <div key={index} className="flex items-center justify-between bg-gray-50 rounded-lg px-4 py-2">
                        <div>
                          <div className="font-medium text-gray-900 text-sm">{item.time}</div>
                          <div className="text-xs text-gray-500">{item.note}</div>
                        </div>
                        <div className="text-cyan-600 font-semibold">{item.amount} ml</div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* CTA */}
                <Link
                  to="/pricing"
                  className="block w-full text-center bg-accent-500 text-white py-3 rounded-xl hover:bg-accent-600 transition-colors font-semibold"
                >
                  Get Full Nutrition Plan
                </Link>
              </div>
            ) : (
              <div className="text-center py-12 text-gray-500">
                <FaTint className="text-5xl mx-auto mb-4 text-gray-300" />
                <p>Enter your details to calculate your daily water needs</p>
              </div>
            )}
          </div>
        </div>

        {/* Info Section */}
        <div className="mt-12 bg-white rounded-2xl shadow-sm p-6 md:p-8 border border-gray-100">
          <div className="flex items-center gap-3 mb-4">
            <FaInfoCircle className="text-cyan-600 text-xl" />
            <h3 className="text-lg font-bold text-gray-900">Why Hydration Matters</h3>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <h4 className="font-semibold text-gray-900 mb-2">Benefits of Proper Hydration</h4>
              <ul className="text-sm text-gray-600 space-y-2">
                <li>Boosts metabolism and supports weight loss</li>
                <li>Improves physical performance and energy</li>
                <li>Enhances cognitive function and focus</li>
                <li>Aids digestion and nutrient absorption</li>
                <li>Helps regulate body temperature</li>
                <li>Supports kidney function and detoxification</li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-gray-900 mb-2">Signs of Dehydration</h4>
              <ul className="text-sm text-gray-600 space-y-2">
                <li>Dark yellow urine</li>
                <li>Feeling thirsty or dry mouth</li>
                <li>Headaches and fatigue</li>
                <li>Dizziness or lightheadedness</li>
                <li>Decreased urine output</li>
                <li>Dry skin</li>
              </ul>
            </div>
          </div>
          <div className="mt-4 p-4 bg-cyan-50 rounded-xl">
            <p className="text-sm text-gray-600">
              <strong>Tip:</strong> Drink water consistently throughout the day rather than large amounts at once.
              Carry a reusable water bottle and set reminders if needed. Foods like fruits and vegetables also contribute to your daily water intake.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WaterIntakeCalculator;
